// Everything beside the asphalt that makes a circuit read as real: mowed grass,
// textured asphalt, gravel traps on the outside of corners, catch fences on
// posts with advertising boards at their foot, tyre walls, grandstands along
// the main straight, and overhead gantries (start lights on the start line,
// sponsor banners elsewhere).
import * as THREE from 'three'

const V3 = THREE.Vector3

export type Sample = { p: THREE.Vector3; t: THREE.Vector3; n: THREE.Vector3 }

export type TracksideContext = {
  scene: THREE.Scene
  samples: Sample[]
  width: number
  centre: THREE.Vector3
  size: number
  // The pit lane side of the main straight: no fence or stands there.
  pitFrom: THREE.Vector3
  pitTo: THREE.Vector3
  pitNormal: THREE.Vector3
  pitReach: number
  gantries: number[]
  startU: number
  straightStands: number
}

function canvasTexture(w: number, h: number, draw: (x: CanvasRenderingContext2D) => void, repeat = true) {
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  draw(canvas.getContext('2d')!)
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  if (repeat) texture.wrapS = texture.wrapT = THREE.RepeatWrapping
  texture.anisotropy = 8
  return texture
}

function noise(x: CanvasRenderingContext2D, w: number, h: number, colors: string[], count: number, size: number) {
  for (let i = 0; i < count; i++) {
    x.fillStyle = colors[i % colors.length]
    x.fillRect(Math.random() * w, Math.random() * h, size * (0.5 + Math.random()), size * (0.5 + Math.random()))
  }
}

export const textures = {
  grass: () => canvasTexture(256, 256, (x) => {
    // Mowing stripes and a little noise.
    x.fillStyle = '#4f9a3e'
    x.fillRect(0, 0, 256, 256)
    x.fillStyle = '#539f41'
    x.fillRect(0, 0, 256, 128)
    noise(x, 256, 256, ['rgba(40,90,30,0.25)', 'rgba(110,170,80,0.2)'], 900, 3)
  }),
  asphalt: () => canvasTexture(256, 256, (x) => {
    x.fillStyle = '#2b2d31'
    x.fillRect(0, 0, 256, 256)
    noise(x, 256, 256, ['rgba(0,0,0,0.28)', 'rgba(90,92,98,0.25)', 'rgba(60,62,66,0.3)'], 3000, 2)
  }),
  gravel: () => canvasTexture(256, 256, (x) => {
    x.fillStyle = '#d8c28d'
    x.fillRect(0, 0, 256, 256)
    noise(x, 256, 256, ['rgba(150,120,70,0.35)', 'rgba(250,235,200,0.4)', 'rgba(120,100,70,0.3)'], 2600, 2.5)
  }),
  verge: () => canvasTexture(64, 64, (x) => {
    x.fillStyle = '#6a6e70'
    x.fillRect(0, 0, 64, 64)
    noise(x, 64, 64, ['rgba(0,0,0,0.2)', 'rgba(160,160,160,0.2)'], 200, 2)
  }),
  fence: () => canvasTexture(64, 64, (x) => {
    // Chain-link diamonds on a transparent ground.
    x.clearRect(0, 0, 64, 64)
    x.strokeStyle = 'rgba(200,205,210,0.85)'
    x.lineWidth = 2
    for (let i = -64; i < 128; i += 16) {
      x.beginPath()
      x.moveTo(i, 0)
      x.lineTo(i + 64, 64)
      x.moveTo(i + 64, 0)
      x.lineTo(i, 64)
      x.stroke()
    }
    x.fillStyle = 'rgba(200,205,210,0.9)'
    x.fillRect(0, 0, 64, 3)
  }),
}

// Advertising board strip: alternating sponsor panels, repeated along a fence.
export function adTexture(names: { name: string; color: string }[]): THREE.CanvasTexture {
  const panel = 256
  return canvasTexture(panel * names.length, 64, (x) => {
    names.forEach((board, i) => {
      x.fillStyle = board.color
      x.fillRect(i * panel, 0, panel, 64)
      const n = parseInt(board.color.replace('#', ''), 16)
      const light = 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255) > 150
      x.fillStyle = light ? '#0E0E0E' : '#F7F7F5'
      x.font = 'italic 800 40px "Saira Condensed", "Arial Narrow", sans-serif'
      x.textAlign = 'center'
      x.textBaseline = 'middle'
      x.fillText(board.name.toUpperCase(), i * panel + panel / 2, 34, panel - 20)
    })
  })
}

// A strip whose inner/outer offsets vary along the track, with UVs in metres.
export function strip(samples: Sample[], offsets: (i: number) => [number, number] | null, y: number, closed = true): THREE.BufferGeometry {
  const pos: number[] = []
  const uv: number[] = []
  let distance = 0
  const last = closed ? samples.length : samples.length - 1
  for (let i = 0; i < last; i++) {
    const a = samples[i]
    const b = samples[(i + 1) % samples.length]
    const step = a.p.distanceTo(b.p)
    const oa = offsets(i)
    const ob = offsets((i + 1) % samples.length)
    if (oa && ob) {
      const a1 = a.p.clone().addScaledVector(a.n, oa[0])
      const a2 = a.p.clone().addScaledVector(a.n, oa[1])
      const b1 = b.p.clone().addScaledVector(b.n, ob[0])
      const b2 = b.p.clone().addScaledVector(b.n, ob[1])
      const quad: [THREE.Vector3, number, number][] = [[a1, distance, oa[0]], [a2, distance, oa[1]], [b2, distance + step, ob[1]], [a1, distance, oa[0]], [b2, distance + step, ob[1]], [b1, distance + step, ob[0]]]
      for (const [v, d, o] of quad) {
        pos.push(v.x, y, v.z)
        uv.push(d / 8, o / 8)
      }
    }
    distance += step
  }
  const g = new THREE.BufferGeometry()
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2))
  g.computeVertexNormals()
  // Strips may wind either way; flip normals that point down.
  const n = g.attributes.normal as THREE.BufferAttribute
  for (let i = 0; i < n.count; i++) if (n.getY(i) < 0) n.setXYZ(i, -n.getX(i), -n.getY(i), -n.getZ(i))
  return g
}

// A vertical wall following the track at a per-sample offset (fence, boards).
// `facing` is the side the wall stands on (+1 right of travel, -1 left): the
// texture runs so text reads left-to-right when seen from the track.
function wall(samples: Sample[], offset: (i: number) => number | null, bottom: number, top: number, uScale: number, facing = 1): THREE.BufferGeometry {
  const pos: number[] = []
  const uv: number[] = []
  let distance = 0
  for (let i = 0; i < samples.length; i++) {
    const a = samples[i]
    const b = samples[(i + 1) % samples.length]
    const oa = offset(i)
    const ob = offset((i + 1) % samples.length)
    const pa = oa === null ? null : a.p.clone().addScaledVector(a.n, oa)
    const pb = ob === null ? null : b.p.clone().addScaledVector(b.n, ob)
    const step = pa && pb ? pa.distanceTo(pb) : a.p.distanceTo(b.p)
    if (pa && pb && step < 12) {
      const quad: [THREE.Vector3, number, number][] = [
        [pa, distance, bottom], [pb, distance + step, bottom], [pb, distance + step, top],
        [pa, distance, bottom], [pb, distance + step, top], [pa, distance, top],
      ]
      for (const [v, d, y] of quad) {
        pos.push(v.x, y, v.z)
        uv.push((facing > 0 ? -d : d) / uScale, (y - bottom) / (top - bottom))
      }
    }
    distance += step
  }
  const g = new THREE.BufferGeometry()
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3))
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2))
  g.computeVertexNormals()
  return g
}

export function addTrackside(ctx: TracksideContext) {
  const { scene, samples, width: W } = ctx
  const N = samples.length
  const look = 8

  // Turn strength and direction at each sample, smoothed.
  const raw = samples.map((s, i) => {
    const t2 = samples[(i + look) % N].t
    const cross = s.t.x * t2.z - s.t.z * t2.x
    return cross
  })
  const turn = raw.map((_, i) => {
    let sum = 0
    for (let k = -10; k <= 10; k++) sum += raw[(i + k + N) % N]
    return sum / 21
  })
  // Gravel on the outside of corners, wider in tighter corners (metres).
  const gravel = turn.map((c) => Math.min(18, Math.max(0, (Math.abs(c) - 0.012) * 260)))
  const outsideSide = turn.map((c) => (c > 0 ? -1 : 1)) // n points right of travel
  // Gravel per side, spread to neighbouring samples with a falloff so traps
  // run into each other through S-bends instead of leaving grass gaps.
  const spread = 60
  const gravelOn = (side: number) => {
    const raw = gravel.map((g, i) => (outsideSide[i] === side ? g : 0))
    return raw.map((_, i) => {
      let best = 0
      for (let k = -spread; k <= spread; k++) best = Math.max(best, raw[(i + k + N) % N] * (1 - Math.abs(k) / (spread + 30)))
      return best
    })
  }
  const gravelBySide = { 1: gravelOn(1), [-1]: gravelOn(-1) } as Record<number, number[]>

  // Near the pit straight on the pit side: leave room for the lane and garages.
  const pitDir = ctx.pitTo.clone().sub(ctx.pitFrom)
  const pitLen = pitDir.length()
  pitDir.normalize()
  const onPitSide = (i: number, side: number) => {
    const s = samples[i]
    if (side * s.n.dot(ctx.pitNormal) <= 0) return false
    const along = s.p.clone().sub(ctx.pitFrom).dot(pitDir)
    return along > -pitLen * 0.35 && along < pitLen * 1.25 && Math.abs(s.p.clone().sub(ctx.pitFrom).dot(ctx.pitNormal)) < 6
  }
  // Points of a fence/wall too close to another part of the track are dropped.
  const coarse = samples.filter((_, i) => i % 3 === 0)
  const clearOfTrack = (p: THREE.Vector3, index: number, margin: number) =>
    coarse.every((s, k) => Math.abs(k * 3 - index) < 40 || Math.abs(k * 3 - index) > N - 40 || s.p.distanceToSquared(p) > (W / 2 + margin) ** 2)

  // ── Surfaces ────────────────────────────────────────────────────────
  const verge = new THREE.Mesh(strip(samples, () => [-W / 2 - 1.4 - 1.6, W / 2 + 1.4 + 1.6], 0.015), new THREE.MeshLambertMaterial({ map: textures.verge() }))
  verge.receiveShadow = true
  scene.add(verge)
  const gravelTex = textures.gravel()
  for (const side of [1, -1]) {
    const widths = gravelBySide[side]
    const mesh = new THREE.Mesh(strip(samples, (i) => {
      const g = widths[i]
      if (g < 0.6) return null
      const from = side * (W / 2 + 3.9)
      const to = side * (W / 2 + 3.9 + g)
      return side > 0 ? [from, to] : [to, from]
    }, 0.025), new THREE.MeshLambertMaterial({ map: gravelTex }))
    mesh.receiveShadow = true
    scene.add(mesh)
  }

  // ── Fences with advertising boards and tyre walls ────────────────────
  const fenceOffset = (i: number, side: number) => side * (W / 2 + 5 + gravelBySide[side][i])
  const fenceMat = new THREE.MeshLambertMaterial({ map: textures.fence(), transparent: true, side: THREE.DoubleSide, alphaTest: 0.2 })
  ;(fenceMat.map as THREE.Texture).repeat.set(1, 1)
  const boardNames = [
    { name: 'LaunchGP', color: '#FF5A1F' }, { name: 'Your brand here', color: '#F3F3F1' }, { name: 'Grand Prix', color: '#14161A' },
    { name: 'Endurance', color: '#4C9AFF' }, { name: 'LaunchGP', color: '#FFD23F' }, { name: 'Launch week', color: '#2BD47D' },
  ]
  const boardMat = new THREE.MeshLambertMaterial({ map: adTexture(boardNames), side: THREE.DoubleSide })
  const postPositions: THREE.Vector3[] = []
  const tyrePositions: THREE.Vector3[] = []
  for (const side of [1, -1]) {
    const keep = samples.map((s, i) => {
      if (onPitSide(i, side)) return false
      const p = s.p.clone().addScaledVector(s.n, fenceOffset(i, side))
      return clearOfTrack(p, i, 3)
    })
    const offset = (shift: number) => (i: number) => (keep[i] ? fenceOffset(i, side) + side * shift : null)
    const fence = new THREE.Mesh(wall(samples, offset(0), 0, 3.6, 3, side), fenceMat)
    const boards = new THREE.Mesh(wall(samples, offset(-0.25), 0, 1.0, 6 * boardNames.length, side), boardMat)
    boards.receiveShadow = true
    scene.add(fence, boards)
    samples.forEach((s, i) => {
      if (!keep[i]) return
      const p = s.p.clone().addScaledVector(s.n, fenceOffset(i, side))
      if (i % 5 === 0) postPositions.push(p)
      if (gravelBySide[side][i] > 7 && i % 2 === 0) tyrePositions.push(s.p.clone().addScaledVector(s.n, fenceOffset(i, side) - side * 0.9))
    })
  }
  const posts = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.07, 0.07, 3.8, 6), new THREE.MeshLambertMaterial({ color: 0x9aa0a6 }), postPositions.length)
  const m = new THREE.Matrix4()
  postPositions.forEach((p, i) => posts.setMatrixAt(i, m.makeTranslation(p.x, 1.9, p.z)))
  scene.add(posts)
  const tyreColors = [0x141416, 0xe0321b, 0xf3f3f1].map((c) => new THREE.Color(c))
  const tyres = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.45, 0.45, 1.05, 12), new THREE.MeshLambertMaterial(), tyrePositions.length)
  tyrePositions.forEach((p, i) => {
    tyres.setMatrixAt(i, m.makeTranslation(p.x, 0.53, p.z))
    tyres.setColorAt(i, tyreColors[Math.floor(i / 3) % 2 ? 0 : (i % 3 === 0 ? 1 : 2)])
  })
  tyres.castShadow = true
  scene.add(tyres)

  // ── Grandstands along the main straight (opposite the pits) ──────────
  const crowdColors = [0xff5a1f, 0x4c9aff, 0xf3f3f1, 0xffd23f, 0x8a8f96, 0xe0321b, 0x2bd47d].map((c) => new THREE.Color(c))
  for (let k = 0; k < ctx.straightStands; k++) {
    const at = ctx.pitFrom.clone().addScaledVector(pitDir, pitLen * (0.12 + (k * 0.76) / Math.max(1, ctx.straightStands - 1)))
    const pos = at.clone().addScaledVector(ctx.pitNormal, -(W / 2 + 14))
    scene.add(grandstand(pos, at, 44, crowdColors))
  }

  // ── Overhead gantries: start lights and sponsor banners ──────────────
  const bannerMats: THREE.MeshLambertMaterial[] = []
  for (const u of ctx.gantries) {
    const s = samples[Math.floor((((u % 1) + 1) % 1) * N)]
    const group = new THREE.Group()
    group.position.copy(s.p)
    group.rotation.y = Math.atan2(s.t.x, s.t.z)
    const span = W + 10
    const lattice = new THREE.MeshLambertMaterial({ color: 0xc9ccd2 })
    for (const x of [-span / 2, span / 2]) {
      const tower = new THREE.Mesh(new THREE.BoxGeometry(0.7, 9, 0.7), lattice)
      tower.position.set(x, 4.5, 0)
      tower.castShadow = true
      group.add(tower)
      for (let y = 1; y < 9; y += 1.5) {
        const brace = new THREE.Mesh(new THREE.BoxGeometry(0.75, 0.08, 0.75), lattice)
        brace.position.set(x, y, 0)
        group.add(brace)
      }
    }
    const bannerMat = new THREE.MeshLambertMaterial({ color: 0xffffff })
    bannerMats.push(bannerMat)
    const banner = new THREE.Mesh(new THREE.BoxGeometry(span, 2.2, 0.35), [lattice, lattice, lattice, lattice, bannerMat, bannerMat])
    banner.position.set(0, 8.2, 0)
    banner.castShadow = true
    group.add(banner)
    if (Math.abs(u - ctx.startU) < 0.002) {
      // Start lights: five columns of red lamps under the banner.
      const panel = new THREE.Mesh(new THREE.BoxGeometry(4.2, 1.1, 0.3), new THREE.MeshLambertMaterial({ color: 0x111214 }))
      panel.position.set(0, 6.4, 0.2)
      group.add(panel)
      const red = new THREE.MeshBasicMaterial({ color: 0xff2a2a })
      for (let c = 0; c < 5; c++) {
        for (const y of [6.65, 6.15]) {
          const lamp = new THREE.Mesh(new THREE.CircleGeometry(0.18, 16), red)
          lamp.position.set(-1.6 + c * 0.8, y, 0.36)
          group.add(lamp)
        }
      }
    }
    scene.add(group)
  }
  return {
    // Gantry banners show the round's sponsors (or LaunchGP).
    setBanners(names: { name: string; tagline: string | null; color: string }[], paint: (name: string, tagline: string | null, color: string) => THREE.Texture) {
      bannerMats.forEach((mat, i) => {
        const b = names[i % Math.max(1, names.length)] ?? { name: 'LaunchGP', tagline: 'the launch grand prix', color: '#FF5A1F' }
        mat.map?.dispose()
        mat.map = paint(b.name, b.tagline, b.color)
        mat.needsUpdate = true
      })
    },
  }
}

function grandstand(pos: THREE.Vector3, facing: THREE.Vector3, length: number, colors: THREE.Color[]): THREE.Group {
  const group = new THREE.Group()
  group.position.copy(pos)
  group.lookAt(facing.x, 0, facing.z)
  const rows = 7
  const perRow = Math.floor(length / 1.05)
  const tierMat = new THREE.MeshLambertMaterial({ color: 0x3a3e45 })
  const crowd = new THREE.InstancedMesh(new THREE.BoxGeometry(0.6, 0.9, 0.5), new THREE.MeshLambertMaterial(), perRow * rows)
  const m = new THREE.Matrix4()
  let k = 0
  for (let r = 0; r < rows; r++) {
    const tier = new THREE.Mesh(new THREE.BoxGeometry(length, 1, 2.2), tierMat)
    tier.position.set(0, r * 0.9 + 0.5, -r * 2.2)
    tier.receiveShadow = true
    group.add(tier)
    for (let c = 0; c < perRow; c++) {
      if (Math.random() < 0.08) continue
      m.makeTranslation(-length / 2 + 0.5 + c * 1.05 + (Math.random() - 0.5) * 0.3, r * 0.9 + 1.45, -r * 2.2)
      crowd.setMatrixAt(k, m)
      crowd.setColorAt(k, colors[Math.floor(Math.random() * colors.length)])
      k++
    }
  }
  crowd.count = k
  group.add(crowd)
  const roof = new THREE.Mesh(new THREE.BoxGeometry(length + 2, 0.4, rows * 2.2 + 2), new THREE.MeshLambertMaterial({ color: 0xf3f3f1 }))
  roof.position.set(0, rows * 0.9 + 5, -(rows * 2.2) / 2 + 0.8)
  roof.castShadow = true
  group.add(roof)
  for (const x of [-length / 2, 0, length / 2]) {
    const column = new THREE.Mesh(new THREE.BoxGeometry(0.5, rows * 0.9 + 5, 0.5), new THREE.MeshLambertMaterial({ color: 0x9aa0a6 }))
    column.position.set(x, (rows * 0.9 + 5) / 2, -rows * 2.2)
    group.add(column)
  }
  return group
}

export { V3 }
